import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PersonFeedback24Regular } from "@fluentui/react-icons";
import { useState } from "react";

const APP_CACHE_PREFIX = "clarifywithai-";

const clearCachesAndReload = async () => {
    try {
        if ("serviceWorker" in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map(registration => registration.unregister()));
        }
        if ("caches" in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.filter(name => name.startsWith(APP_CACHE_PREFIX)).map(name => caches.delete(name)));
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to clear caches/service workers", error);
    } finally {
        window.location.reload();
    }
};

const FEEDBACK_FORM_URL =
    "https://forms.cloud.microsoft/pages/responsepage.aspx?id=_yqYY2z7IkyXO3DkrPtj5kSYMmOBhjhPmikxHmLbhXJURVpTSDVYU1dLTUpWRElMS0NKUk5XUlJRNC4u&route=shorturl";

export const RouteErrorBoundary = () => {
    const error = useRouteError();
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    let message = t("errorBoundary.unexpected", "An unexpected error occurred.");
    let name = "Error";
    let stack: string | undefined;
    if (isRouteErrorResponse(error)) {
        message = `${error.status} ${error.statusText}`;
        name = "RouteError";
    } else if (error instanceof Error) {
        message = error.message;
        name = error.name;
        stack = error.stack;
    }

    // eslint-disable-next-line no-console
    console.error("RouteErrorBoundary caught:", error);

    const timestamp = new Date().toISOString();
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";

    const errorDetails = [
        `Time: ${timestamp}`,
        `URL: ${pageUrl}`,
        `Error: ${name}: ${message}`,
        stack ? `Stack:\n${stack}` : undefined,
        `User agent: ${userAgent}`
    ]
        .filter(Boolean)
        .join("\n");

    const handleCopyDetails = () => {
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== "function") {
            console.error("Clipboard API is not available in this context.");
            return;
        }
        navigator.clipboard
            .writeText(errorDetails)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy error details: ", err));
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "50vh",
                textAlign: "center",
                padding: "2rem",
                gap: "1rem"
            }}
        >
            <h2>{t("errorBoundary.title", "Something went wrong")}</h2>
            <p style={{ color: "#666", maxWidth: "560px" }}>
                <strong>
                    {name}: {message}
                </strong>
            </p>
            <details style={{ maxWidth: "640px", textAlign: "left", color: "#666" }}>
                <summary style={{ cursor: "pointer" }}>{t("errorBoundary.showDetails", "Show technical details")}</summary>
                <pre
                    style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        background: "#f5f5f5",
                        padding: "0.75rem",
                        borderRadius: "4px",
                        fontSize: "0.8rem"
                    }}
                >
                    {errorDetails}
                </pre>
            </details>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        padding: "0.5rem 1.25rem",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        cursor: "pointer",
                        background: "#fff"
                    }}
                >
                    {t("errorBoundary.reload", "Reload page")}
                </button>
                <button
                    onClick={clearCachesAndReload}
                    style={{
                        padding: "0.5rem 1.25rem",
                        borderRadius: "4px",
                        border: "none",
                        cursor: "pointer",
                        background: "#0078d4",
                        color: "#fff"
                    }}
                >
                    {t("errorBoundary.clearCacheReload", "Clear cache & reload")}
                </button>
                <button
                    onClick={handleCopyDetails}
                    style={{
                        padding: "0.5rem 1.25rem",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        cursor: "pointer",
                        background: "#fff"
                    }}
                >
                    {copied ? t("errorBoundary.copied", "Copied!") : t("errorBoundary.copyDetails", "Copy error details")}
                </button>
            </div>
            <a
                href={FEEDBACK_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", color: "#0078d4", marginTop: "0.5rem" }}
            >
                <PersonFeedback24Regular />
                {t("feedbackText", "Share your feedback or questions")} <span>{t("feedbackHere", "here.")}</span>
            </a>
        </div>
    );
};

export default RouteErrorBoundary;
